import { FlexibleConnectedPositionStrategy } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import {
  ChangeDetectorRef,
  ComponentRef,
  ElementRef,
  Inject,
  Injectable,
  Injector,
  OnDestroy,
  Optional,
  SkipSelf,
  Type,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';

import { BehaviorSubject, EMPTY, merge, Observable, Subject } from 'rxjs';
import { debounceTime, filter, take, takeUntil } from 'rxjs/operators';

import {
  ConnectedOverlayDirective,
  OverlayOriginDirective,
} from '../core/overlay/overlay-directives';
import { ComponentTooltipResizeConsumer } from './resize-providers';
import {
  ArrowPosition,
  ComponentPositionPair,
  ComponentTooltipPosition,
  ComponentTooltipServiceConfig,
  ObserveOnElement,
} from './types';

@Injectable()
export class ComponentTooltipService<
  C extends Type<{
    ngOnDestroy$: Observable<void>;
    updateArrow: (arrowPosition: ArrowPosition) => void;
  }>,
  T,
> implements OnDestroy
{
  public data$: BehaviorSubject<T | null> = new BehaviorSubject(null);

  private component: C;
  private componentData: T;
  private componentRef: ComponentRef<{
    ngOnDestroy$: Observable<void>;
    updateArrow: (arrowPosition: ArrowPosition) => void;
  }> | null = null;
  private config: ComponentTooltipServiceConfig;
  private destroyTooltip$ = new Subject<void>();
  private mutationObserver: MutationObserver;

  constructor(
    private elementRef: ElementRef,
    private injector: Injector,
    private router: Router,
    private connectedOverlayDirective: ConnectedOverlayDirective,
    @SkipSelf() private _parentCdRef: ChangeDetectorRef,
    @Optional() @Inject('overlayLevel') private _level: number,
    @Optional() private componentTooltipResize?: ComponentTooltipResizeConsumer,
  ) {}

  public ngOnDestroy(): void {
    this.componentRef = null;
    this.closeTooltip();
  }

  public hasConfig(): boolean {
    return !!this.config;
  }

  public setConfig(config: ComponentTooltipServiceConfig): void {
    this.config = config;
  }

  public setComponent(component: C): void {
    this.component = component;
  }

  public setComponentData(data: T): void {
    this.componentData = data;
    this.data$.next(data);
  }

  public openTooltip(originRef?: ElementRef): ComponentRef<InstanceType<C>> {
    if (originRef) {
      this.setupConnectedOverlayDirective();
      this.watchPositionUpdateEvents();
    }

    const portal = new ComponentPortal(this.component, null, this.injector);
    this.connectedOverlayDirective.cdkConnectedOverlay.positions = this.getPositions();
    this.connectedOverlayDirective.origin = new OverlayOriginDirective(
      originRef || this.elementRef,
    );
    // TODO TEST
    this.connectedOverlayDirective.cdkConnectedOverlay['_templatePortal'] = portal;
    this.componentRef = <ComponentRef<InstanceType<C>>>(
      this.connectedOverlayDirective.toggleOverlay(true)
    );
    this.connectedOverlayDirective.positionChange
      .pipe(take(1))
      .subscribe(() => this.updateTooltipPosition());
    this.startObserveOnElement();
    this.updateTooltipPosition();
    return <ComponentRef<InstanceType<C>>>this.componentRef;
  }

  public closeTooltip(): void {
    this.destroyTooltip$.next();

    this.connectedOverlayDirective.open = false;
    this.stopObserveOnElement();
  }

  private setupConnectedOverlayDirective() {
    this.connectedOverlayDirective.cdkConnectedOverlay.hasBackdrop = this.config.hasBackdrop;
    this.connectedOverlayDirective.cdkConnectedOverlay.backdropClass =
      this.config.backdropClass || '';
    this.connectedOverlayDirective.autoCloseOnUnpreventedClick = false;
    this.connectedOverlayDirective.position = Object.values(ComponentTooltipPosition);
    this.connectedOverlayDirective.overlayUpdatePositions();
    this._parentCdRef.detectChanges();
  }

  private getPositions() {
    return <ComponentPositionPair[]>Object.values(this.config.positions);
  }

  private updateTooltipPosition() {
    const availablePositions = this.getPositions();
    const positionStrategy = <FlexibleConnectedPositionStrategy>(
      this.connectedOverlayDirective.cdkConnectedOverlay['_position']
    );
    if (!positionStrategy) return;

    positionStrategy.withPositions(availablePositions);
    this.connectedOverlayDirective.updatePositionWithoutRedrawing();
    const position = positionStrategy['_lastPosition'];

    if (position) {
      this.lockTooltipPosition(position);
      this.connectedOverlayDirective.cdkConnectedOverlay.offsetX = position.offsetX;
      this.connectedOverlayDirective.cdkConnectedOverlay.offsetY = position.offsetY;
      this.connectedOverlayDirective.cdkConnectedOverlay.overlayRef.updatePosition();

      if (this.componentRef) {
        const { instance, changeDetectorRef } = this.componentRef;
        instance.updateArrow(position.arrowPosition);
        changeDetectorRef.detectChanges();
      }
    }
  }

  private watchPositionUpdateEvents(): void {
    merge<unknown[]>(
      this.router.events.pipe(filter(event => event instanceof NavigationEnd)),
      this.componentTooltipResize ?? EMPTY,
    )
      .pipe(debounceTime(5), takeUntil(this.destroyTooltip$))
      .subscribe(() => this.updateTooltipPosition());
  }

  private getOverlayElement(): HTMLElement {
    return this.connectedOverlayDirective.cdkConnectedOverlay.overlayRef.overlayElement;
  }

  private lockTooltipPosition(position: ComponentPositionPair) {
    const { positionStrategy } = this.connectedOverlayDirective.cdkConnectedOverlay;
    if (positionStrategy) {
      this.connectedOverlayDirective.positions = [position];
      positionStrategy._preferredPositions = [position];
    }
  }

  private startObserveOnElement(): void {
    if (!this.config.observeOnElement) return;

    this.mutationObserver = new MutationObserver(() => this.updateTooltipPosition());

    // eslint-disable-next-line default-case
    switch (this.config.observeOnElement) {
      case ObserveOnElement.Self: {
        this.mutationObserver.observe(this.elementRef.nativeElement, {
          attributes: true,
        });
      }
      case ObserveOnElement.Parent: {
        this.mutationObserver.observe(this.elementRef.nativeElement.parentElement, {
          attributes: true,
          subtree: true,
        });
      }
    }
  }

  private stopObserveOnElement(): void {
    if (this.config?.observeOnElement && this.mutationObserver) this.mutationObserver.disconnect();
  }
}
